import { useEffect, useMemo } from 'react'

import { NavMenuContentType, getNavMenuContent } from '@hhgtech/hhg-components'
import { FooterInfoMarryBaby } from '@hhgtech/hhg-components/types'
import { cleanUpUndefined, formatBabyPoopPage } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { callApi, checkMobile, getApiPath } from 'api/utils'
import { NavigatorLayout } from 'components'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants'
import { BabyPoopPage as BabyPoopPageType } from 'interfaces/types'
import { GetServerSideProps } from 'next'
import { useIntl } from 'react-intl'

import { addResultToLink } from '../helpers'
import { BabyPoopDetail } from './detail'
import { StyledBabyPoop } from './index.styled'
import { BabyPoopIntroduce } from './introduce'
import { BABY_POOP_PAGE_TYPE } from './types'

export type BabyPoopPageProps = {
  pageType: string
  content: {
    title: string
    description: string
  }[]
  babyPoopPage: BabyPoopPageType
  isMobileSsr: boolean
  footerInfo?: FooterInfoMarryBaby
  dataNavMenuContent?: NavMenuContentType
}

const BabyPoopPage = ({
  footerInfo,
  dataNavMenuContent,
  babyPoopPage,
}: BabyPoopPageProps) => {
  const { locale = 'en-PH', query, router } = useRouterExtend()
  const { formatMessage: f } = useIntl()

  const babyPoopPageType = useMemo<
    typeof BABY_POOP_PAGE_TYPE[keyof typeof BABY_POOP_PAGE_TYPE]
  >(() => {
    if (
      query.type &&
      babyPoopPage.result.find((poop) => query.type === poop.name)
    ) {
      return BABY_POOP_PAGE_TYPE.DETAIL
    } else {
      return BABY_POOP_PAGE_TYPE.INTRO
    }
  }, [locale, router.asPath])
  useEffect(() => {
    if (babyPoopPageType === BABY_POOP_PAGE_TYPE.DETAIL) {
      if (!router.asPath.includes('?result=1')) {
        const newPath = addResultToLink(router.asPath)
        router.push(newPath, newPath, { shallow: true })
      }
    }
  }, [babyPoopPageType])

  return (
    <NavigatorLayout
      description={f({
        id: 'babyPoop.description',
      })}
      SEOData={{
        schemaData: f({
          id: 'babyPoop.seoTitle',
        }),
        apiMetaData: babyPoopPage.metaTag,
        isIndexable: true,
        hasNoAmp: true,
        hasNoCanonical: true,
      }}
      footerMenu={footerInfo}
      dataNavMenuContent={dataNavMenuContent}
      activeMenuService="tools"
      isSponsored={babyPoopPage.isSponsored}
      // sponsoredBy={{ ...babyPoopPage.sponsor } || null}
      // sponsoredType="content"
    >
      <StyledBabyPoop>
        {babyPoopPageType === BABY_POOP_PAGE_TYPE.INTRO && (
          <BabyPoopIntroduce babyPoopPage={babyPoopPage} />
        )}

        {babyPoopPageType === BABY_POOP_PAGE_TYPE.DETAIL && (
          <BabyPoopDetail babyPoopPage={babyPoopPage} />
        )}
      </StyledBabyPoop>
    </NavigatorLayout>
  )
}

const getServerSideProps: GetServerSideProps<BabyPoopPageProps> = async (
  context,
) => {
  const { req } = context

  const userAgent = `${req.headers['user-agent']}`
  const isMobileSsr = checkMobile(userAgent)

  const [babyPoopPageRes, footerInfo, dataNavMenuContent] = await Promise.all([
    callApi(getApiPath(PATHS.GET_HEALTH_TOOL_BABY_POOP), 'GET'),
    callApi(getApiPath(PATHS.GET_DYNAMIC_FOOTER), 'GET'),
    getNavMenuContent('vi-VN_MB'),
  ])

  if (
    !babyPoopPageRes ||
    babyPoopPageRes.status !== 1 ||
    !babyPoopPageRes?.data
  ) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    }
  }

  const babyPoopPage = formatBabyPoopPage(
    babyPoopPageRes?.data,
  ) as BabyPoopPageType

  return {
    props: {
      pageType: HEALTH_TOOL_TYPE.BABY_POOP,
      content: [],
      isMobileSsr,
      babyPoopPage,
      footerInfo: footerInfo?.data || null,
      dataNavMenuContent: cleanUpUndefined(dataNavMenuContent) || {},
    },
  }
}

export { BabyPoopPage, getServerSideProps }
